import { CREDIT_CONTROLLER_ASSIGNEE, UNASSIGNED_ASSIGNEE } from '@/constants/dispute-types';

export interface AssigneeCandidate {
  id: string;
  firstName: string;
  lastName: string;
}

export interface StageAssignee {
  /** Explicit user configured on the stage, if any. */
  assignedToUserId?: number;
  /** Stage is configured to use the customer's credit controller; the backend resolves the user. */
  usesCreditController: boolean;
}

/**
 * Reads the assigned user configured on a workflow stage's serialised properties.
 * A `CREDIT_CONTROLLER` sentinel is not resolved client-side: the backend assigns
 * the customer's credit controller when the dispute is created or moved to the level.
 */
export function readStageAssignee(
  properties: string | null | undefined,
  users: AssigneeCandidate[] = [],
): StageAssignee {
  if (!properties) {
    return { usesCreditController: false };
  }

  let assignedUser: unknown;
  try {
    assignedUser = JSON.parse(properties)?.allowedAssigneeRoles?.[0];
  } catch {
    return { usesCreditController: false };
  }

  if (typeof assignedUser !== 'string' || assignedUser === '' || assignedUser === UNASSIGNED_ASSIGNEE) {
    return { usesCreditController: false };
  }

  if (assignedUser === CREDIT_CONTROLLER_ASSIGNEE) {
    return { usesCreditController: true };
  }

  const parsed = parseInt(assignedUser, 10);
  if (!Number.isNaN(parsed)) {
    return { assignedToUserId: parsed, usesCreditController: false };
  }

  const matchedUser = users.find((u) => `${u.firstName} ${u.lastName}` === assignedUser);
  return {
    assignedToUserId: matchedUser ? parseInt(matchedUser.id, 10) : undefined,
    usesCreditController: false,
  };
}
